// /intake — Combined new-patient intake flow.
// Walks a new patient through Consent → Privacy Practices Ack in one
// sitting. Each step lives on its dedicated page but this URL is what
// you text/email new patients as a single "start here" link.

import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, PenLine, ArrowRight, CheckCircle2 } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";

export default function Intake() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const [params] = useSearchParams();
  const patientName = params.get("name") || "";

  const steps = [
    {
      icon: PenLine,
      title: "Consent for services",
      time: "3 min",
      body: "Read the informed consent, then sign electronically with your finger or mouse.",
      href: "/consent",
      testId: "intake-step-consent",
    },
    {
      icon: ShieldCheck,
      title: "Privacy Practices acknowledgment",
      time: "2 min",
      body: "Review the HIPAA Notice of Privacy Practices and confirm receipt.",
      href: "/privacy",
      testId: "intake-step-privacy",
    },
  ];

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="intake-page">
      <Navbar />
      <section className="pt-48 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <p className="overline text-[#9F5C45] mb-3">Welcome to {brand}</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1C1917] mb-4">
            {patientName ? `Hi ${patientName.split(" ")[0]} — ` : ""}
            <em className="text-[#3A5A40]">before your first session.</em>
          </h1>
          <p className="text-[#57534E] max-w-2xl mb-12">
            Just a couple of short forms to save time at your first appointment. Each step gives you a
            signed PDF for your records. About five minutes total.
          </p>

          <div className="space-y-4">
            {steps.map((s, i) => (
              <Link
                key={s.href}
                to={s.href}
                data-testid={s.testId}
                className="group flex items-start gap-5 border border-[#E2DCD0] bg-white p-6 hover:border-[#3A5A40] transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-[#3A5A40]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#3A5A40] transition-colors">
                  <s.icon className="w-5 h-5 text-[#3A5A40] group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="overline text-[#9F5C45]">Step {i + 1} · {s.time}</p>
                  </div>
                  <h2 className="font-serif text-xl md:text-2xl text-[#1C1917] mb-1">{s.title}</h2>
                  <p className="text-sm text-[#57534E]">{s.body}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#9F5C45] group-hover:text-[#3A5A40] mt-1 flex-shrink-0" />
              </Link>
            ))}
          </div>

          <div className="mt-12 p-6 bg-[#3A5A40]/5 border border-[#3A5A40]/20 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#3A5A40] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#1C1917]">
              Each form saves automatically once you sign. You can revisit this page anytime — completed steps will
              still be on file with your care team.
            </p>
          </div>

          <p className="text-xs text-[#86827B] mt-8">
            Trouble signing? Call the clinic at <a href={`tel:${site?.phone || "203-437-8193"}`} className="text-[#3A5A40] hover:underline">{site?.phone || "203-437-8193"}</a> and we&apos;ll walk you through it.
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
